import React, { useState } from "react";
import {
  Table,
  Button,
  Space,
  Select,
  Card,
  Typography,
  Tag,
  Modal,
  Form,
  Row,
  Col,
  message,
  Descriptions,
  Badge,
  Spin,
  Tooltip,
  Statistic,
  Tabs,
  List,
  Avatar,
  Divider,
  Alert,
  Input,
} from "antd";
import {
  EyeOutlined,
  ReloadOutlined,
  DollarOutlined,
  CalculatorOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  UserOutlined,
  FileTextOutlined,
  TeamOutlined,
  BarChartOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  commissionService,
  type Commission,
  type GetCommissionsParams,
  type CalculateCommissionRequest,
} from "../services/commissionService";
import moment from "moment";
import { employeeService } from "../services/employeeService";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const CommissionsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useState<GetCommissionsParams>({
    page: 1,
    limit: 10,
  });
  const [selectedCommission, setSelectedCommission] = useState<Commission | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [calculateModalVisible, setCalculateModalVisible] = useState(false);
  const [payModalVisible, setPayModalVisible] = useState(false);
  const [summaryModalVisible, setSummaryModalVisible] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(moment().month() + 1);
  const [selectedYear, setSelectedYear] = useState(moment().year());
  const [exportLoading, setExportLoading] = useState(false);
  const [calculateForm] = Form.useForm();
  const [payForm] = Form.useForm();

  // Fetch employees
  const { data: employeesData } = useQuery(
    ["employees"],
    () => employeeService.getEmployees(),
    { keepPreviousData: true }
  );

  // Query สำหรับดึงข้อมูลค่าคอมมิชชัน
  const {
    data: commissionsResponse,
    isLoading,
    refetch,
  } = useQuery(
    ["commissions", searchParams],
    () => commissionService.getCommissions(searchParams),
    {
      keepPreviousData: true,
      onError: (error: any) => {
        message.error("ບໍ່ສາມາດໂຫຼດຂໍ້ມູນຄ່າຄອມມິດຊັ່ນໄດ້");
        console.error("Error fetching commissions:", error);
      },
    }
  );

  // Query สำหรับสรุปค่าคอมมิชชันรายเดือน
  const { data: summaryData, isLoading: isSummaryLoading } = useQuery(
    ["commissionSummary", selectedMonth, selectedYear],
    () => commissionService.getCommissionSummary(selectedMonth, selectedYear),
    {
      enabled: summaryModalVisible,
      onError: (error: any) => {
        message.error("ບໍ່ສາມາດໂຫຼດຂໍ້ມູນໄດ້");
        console.error("Error fetching commission summary:", error);
      },
    }
  );

  // Mutation สำหรับคำนวณค่าคอมมิชชัน
  const calculateCommissionMutation = useMutation(
    (data: CalculateCommissionRequest) =>
      commissionService.calculateMonthlyCommission(data),
    {
      onSuccess: () => {
        message.success("ຄຳນວນຄ່າຄອມມິດຊັ່ນສຳເລັດ");
        queryClient.invalidateQueries(["commissions"]);
        queryClient.invalidateQueries(["commissionSummary"]);
        setCalculateModalVisible(false);
        calculateForm.resetFields();
      },
      onError: (error: any) => {
        message.error(
          error?.response?.data?.message || "ບໍ່ສາມາດຄຳນວນຄ່າຄອມມິດຊັ່ນໄດ້"
        );
      },
    }
  );

  // Mutation สำหรับจ่ายค่าคอมมิชชัน
  const payCommissionMutation = useMutation(
    ({ id, data }: { id: string; data: any }) =>
      commissionService.payCommission(id, data),
    {
      onSuccess: () => {
        message.success("ຈ່າຍຄ່າຄອມມິດຊັ່ນສຳເລັດ");
        queryClient.invalidateQueries(["commissions"]);
        queryClient.invalidateQueries(["commissionSummary"]);
        setPayModalVisible(false);
        setSelectedCommission(null);
        payForm.resetFields();
      },
      onError: (error: any) => {
        message.error(
          error?.response?.data?.message || "ບໍ່ສາມາດຈ່າຍຄ່າຄອມມິດຊັ່ນໄດ້"
        );
      },
    }
  );

  // ฟังก์ชัน Export to Excel
  const exportToExcel = () => {
    if (!commissionsResponse?.data?.commissions?.length && !summaryData?.data?.commissions?.length) {
      message.error("ບໍ່ມີຂໍ້ມູນສຳລັບສົ່ງອອກ excel");
      return;
    }

    setExportLoading(true);
    try {
      const workbook = XLSX.utils.book_new();

      // รายงานค่าคอมมิชชัน (ตารางหลัก)
      const commissionSheetData = commissionsResponse?.data?.commissions?.map((item: Commission) => ({
        ພະນັກງານ: `${item.employee?.name} (${item.employee?.employeeId})`,
        ເດືອນ: commissionService.getMonthNameTH(item.month),
        ປີ: item.year,
        ຄ່າຄອມບໍລິການ: getTotalAmount(item.totalServiceCommission),
        ຄ່າຄອມແນະນຳ: getTotalAmount(item.totalReferralCommission),
        ຄ່າໂອທີ: getTotalAmount(item.totalOvertimeCommission),
        ຍອດລວມ: getTotalAmount(item.grandTotal),
        ສະຖານະ: item.isPaid ? "ຈ່າຍແລ້ວ" : "ຍັງບໍ່ຈ່າຍ",
        ວັນທີຈ່າຍ: item.isPaid && item.paidAt ? moment(item.paidAt).format("DD/MM/YYYY") : "",
      })) || [];
      const commissionSheet = XLSX.utils.json_to_sheet([
        {
          ພະນັກງານ: "ສະຫຼຸບ",
          ເດືອນ: "",
          ປີ: "",
          ຄ່າຄອມບໍລິການ: "",
          ຄ່າຄອມແນະນຳ: "",
          ຄ່າໂອທີ: "",
          ຍອດລວມ: new Intl.NumberFormat("lo-LA").format(
            commissionsResponse?.data?.commissions
              ?.filter((c: any) => !c.isPaid)
              ?.reduce((sum: any, c: any) => sum + c.grandTotal.LAK, 0) || 0
          ) + " ₭",
          ສະຖານະ: `ຈ່າຍແລ້ວ: ${commissionsResponse?.data?.commissions?.filter((c: any) => c.isPaid).length || 0}`,
          ວັນທີຈ່າຍ: `ຍັງບໍ່ຈ່າຍ: ${commissionsResponse?.data?.commissions?.filter((c: any) => !c.isPaid).length || 0}`,
        },
        {},
        ...commissionSheetData,
      ]);
      XLSX.utils.book_append_sheet(workbook, commissionSheet, "ລາຍງານຄ່າຄອມມິດຊັ່ນ");

      // รายงานสรุปรายเดือน
      if (summaryData?.data?.commissions?.length) {
        const summarySheetData = summaryData.data.commissions.map((item: any) => ({
          พนักงาน: `${item.employee?.name} (${item.employee?.employeeId})`,
          ยอดລວມ: getTotalAmount(item.grandTotal),
          สถานะ: item.isPaid ? "ຈ່າຍແລ້ວ" : "ຍັງບໍ່ຈ່າຍ",
        }));
        const summarySheet = XLSX.utils.json_to_sheet([
          {
            พนักงาน: "ສະຫຼຸບ",
            ยอดລວມ: new Intl.NumberFormat("lo-LA").format(summaryData.data.summary.totalAmount.LAK) + " ₭",
            สถานะ: `ພະນັກງານ: ${summaryData.data.summary.totalEmployees}, ຈ່າຍແລ້ວ: ${summaryData.data.summary.totalPaid}, ຍັງບໍ່ຈ່າຍ: ${summaryData.data.summary.totalUnpaid}`,
          },
          {},
          ...summarySheetData,
        ]);
        XLSX.utils.book_append_sheet(workbook, summarySheet, "ສະຫຼຸບປະຈຳເດືອນ");
      }

      // สร้างไฟล์และดาวน์โหลด
      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const data = new Blob([excelBuffer], { type: "application/octet-stream" });
      saveAs(data, `commissions_${searchParams.month || selectedMonth}_${searchParams.year || selectedYear}.xlsx`);
      message.success("ສົ່ງອອກ Excel ສຳເລັດ");
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      message.error("ເກີດຂໍ້ຜິດພາດໃນການສົ່ງອອກ Excel");
    } finally {
      setExportLoading(false);
    }
  };

  const handleViewDetails = (commission: Commission) => {
    setSelectedCommission(commission);
    setDetailModalVisible(true);
  };

  const handlePayCommission = (commission: Commission) => {
    setSelectedCommission(commission);
    setPayModalVisible(true);
  };

  const handleCalculateSubmit = (values: any) => {
    calculateCommissionMutation.mutate(values);
  };

  const handlePaySubmit = (values: any) => {
    if (!selectedCommission) return;
    payCommissionMutation.mutate({ id: selectedCommission._id, data: values });
  };

  const handleEmployeeFilter = (employee?: string) => {
    setSearchParams((prev) => ({ ...prev, employee, page: 1 }));
  };

  const handleStatusFilter = (isPaid?: boolean) => {
    setSearchParams((prev) => ({ ...prev, isPaid, page: 1 }));
  };

  const handleMonthYearFilter = (month?: number, year?: number) => {
    setSearchParams((prev) => ({ ...prev, month, year, page: 1 }));
  };

  const handleTableChange = (pagination: any) => {
    setSearchParams((prev) => ({
      ...prev,
      page: pagination.current,
      limit: pagination.pageSize,
    }));
  };

  const getCurrencySymbol = (currency: string) => {
    const symbols = { LAK: "₭", THB: "฿", USD: "$" };
    return symbols[currency as keyof typeof symbols] || currency;
  };

  const formatCurrency = (amount: number, currency: string) => {
    return `${new Intl.NumberFormat("lo-LA").format(amount)} ${getCurrencySymbol(currency)}`;
  };

  const getTotalAmount = (totals: { LAK: number; THB: number; USD: number }) => {
    const nonZero = Object.entries(totals).filter(([_, amount]) => amount > 0);
    return nonZero
      .map(([currency, amount]) => formatCurrency(amount, currency))
      .join(" + ");
  };

  const columns = [
    {
      title: "ພະນັກງານ",
      dataIndex: "employee",
      key: "employee",
      render: (employee: any) => (
        <div>
          <div style={{ fontWeight: 500, display: "flex", alignItems: "center" }}>
            <UserOutlined style={{ marginRight: 4, color: "#1890ff" }} />
            {employee?.name || "N/A"}
          </div>
          <div style={{ fontSize: "12px", color: "#666" }}>
            ID: {employee?.employeeId || "N/A"} | {employee?.role || "N/A"}
          </div>
        </div>
      ),
    },
    {
      title: "ເດືອນ/ປີ",
      key: "monthYear",
      width: 120,
      render: (_: any, record: Commission) => (
        <div style={{ textAlign: "center" }}>
          <div style={{ fontWeight: 500 }}>
            {commissionService.getMonthNameTH(record.month)}
          </div>
          <div style={{ fontSize: "12px", color: "#666" }}>{record.year}</div>
        </div>
      ),
    },
    {
      title: "ຄ່າຄອມບໍລິການ",
      dataIndex: "totalServiceCommission",
      key: "serviceCommission",
      width: 150,
      render: (totals: any) => (
        <div style={{ fontSize: "12px" }}>
          {getTotalAmount(totals) || "0 ₭"}
        </div>
      ),
    },
    {
      title: "ຄ່າຄອມແນະນຳ",
      dataIndex: "totalReferralCommission",
      key: "referralCommission",
      width: 150,
      render: (totals: any) => (
        <div style={{ fontSize: "12px" }}>
          {getTotalAmount(totals) || "0 ₭"}
        </div>
      ),
    },
    {
      title: "ຄ່າໂອທີ",
      dataIndex: "totalOvertimeCommission",
      key: "overtimeCommission",
      width: 120,
      render: (totals: any) => (
        <div style={{ fontSize: "12px" }}>
          {getTotalAmount(totals) || "0 ₭"}
        </div>
      ),
    },
    {
      title: "ຍອດລວມ",
      dataIndex: "grandTotal",
      key: "grandTotal",
      width: 150,
      render: (totals: any) => (
        <div style={{ fontWeight: 500, color: "#f56a00" }}>
          {getTotalAmount(totals) || "0 ₭"}
        </div>
      ),
      sorter: true,
    },
    {
      title: "ສະຖານະ",
      key: "status",
      width: 120,
      render: (_: any, record: Commission) => (
        <div>
          <Badge
            status={record.isPaid ? "success" : "warning"}
            text={record.isPaid ? "ຈ່າຍແລ້ວ" : "ຍັງບໍ່ຈ່າຍ"}
          />
          {record.isPaid && record.paidAt && (
            <div style={{ fontSize: "11px", color: "#666", marginTop: 2 }}>
              {moment(record.paidAt).format("DD/MM/YYYY")}
            </div>
          )}
        </div>
      ),
      filters: [
        { text: "ຈ່າຍແລ້ວ", value: true },
        { text: "ຍັງບໍ່ຈ່າຍ", value: false },
      ],
    },
    {
      title: "ຈັດການ",
      key: "actions",
      width: 200,
      render: (_: any, record: Commission) => (
        <Space>
          <Tooltip title="ເບິ່ງລາຍລະອຽດ">
            <Button
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record)}
            />
          </Tooltip>
          {!record.isPaid && (
            <Tooltip title="ຈ່າຍຄ່າຄອມມິດຊັ່ນ">
              <Button
                size="small"
                icon={<WalletOutlined />}
                onClick={() => handlePayCommission(record)}
                loading={payCommissionMutation.isLoading}
                style={{
                  backgroundColor: "#52c41a",
                  borderColor: "#52c41a",
                  color: "white",
                }}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const commissions = commissionsResponse?.data?.commissions || [];
  const pagination = commissionsResponse?.data?.pagination;

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          <DollarOutlined style={{ marginRight: 8 }} />
          ຈັດການຄ່າຄອມມິດຊັ່ນ
        </Title>
        <Space>
          <Button
            icon={<BarChartOutlined />}
            onClick={() => setSummaryModalVisible(true)}
          >
            ສະຫຼຸບປະຈຳເດືອນ
          </Button>
          <Button
            icon={<CalculatorOutlined />}
            onClick={() => setCalculateModalVisible(true)}
            type="primary"
          >
            ຄຳນວນຄ່າຄອມມິດຊັ່ນ
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => refetch()}
            loading={isLoading}
          >
            ຮີເຟຣ
          </Button>
          <Button
            icon={<FileTextOutlined />}
            onClick={exportToExcel}
            loading={exportLoading}
            disabled={isLoading || isSummaryLoading}
          >
            ສົ່ງອອກ Excel
          </Button>
        </Space>
      </div>

      {/* Summary Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="ຄ່າຄອມທັງໝົດ"
              value={pagination?.total || 0}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="ຈ່າຍແລ້ວ"
              value={commissions.filter((c: any) => c.isPaid).length}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: "#3f8600" }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="ຍັງບໍ່ຈ່າຍ"
              value={commissions.filter((c: any) => !c.isPaid).length}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: "#faad14" }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="ຍອດລວມຄົງຄ້າງ"
              value={commissions
                .filter((c: any) => !c.isPaid)
                .reduce((sum: any, c: any) => sum + c.grandTotal.LAK, 0)
                .toLocaleString()}
              suffix="₭"
              prefix={<WalletOutlined />}
              valueStyle={{ color: "#cf1322" }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={8} md={6}>
            <Select
              placeholder="ເລືອກພະນັກງານ"
              allowClear
              style={{ width: "100%" }}
              onChange={handleEmployeeFilter}
            >
              {employeesData?.data.employees?.map((emp: any) => (
                <Option key={emp._id} value={emp._id}>
                  {emp.name} ({emp.employeeId})
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Select
              placeholder="ສະຖານະການຈ່າຍ"
              allowClear
              style={{ width: "100%" }}
              onChange={handleStatusFilter}
            >
              <Option value={false}>ຍັງບໍ່ຈ່າຍ</Option>
              <Option value={true}>ຈ່າຍແລ້ວ</Option>
            </Select>
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Select
              placeholder="ເລືອກເດືອນ"
              style={{ width: "100%" }}
              onChange={(month) =>
                handleMonthYearFilter(month, searchParams.year)
              }
              allowClear
            >
              {Array.from({ length: 12 }, (_, i) => (
                <Option key={i + 1} value={i + 1}>
                  {commissionService.getMonthNameTH(i + 1)}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Select
              placeholder="ເລືອກປີ"
              style={{ width: "100%" }}
              onChange={(year) =>
                handleMonthYearFilter(searchParams.month, year)
              }
              allowClear
            >
              {Array.from({ length: 5 }, (_, i) => {
                const year = moment().year() - i;
                return (
                  <Option key={year} value={year}>
                    {year}
                  </Option>
                );
              })}
            </Select>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={commissions}
          rowKey="_id"
          loading={isLoading}
          onChange={handleTableChange}
          pagination={{
            current: pagination?.current || 1,
            pageSize: searchParams.limit || 10,
            total: pagination?.total || 0,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} ຈາກ ${total} ລາຍການ`,
          }}
        />
      </Card>

      {/* Commission Detail Modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center" }}>
            <DollarOutlined style={{ marginRight: 8 }} />
            ລາຍລະອຽດຄ່າຄອມມິດຊັ່ນ
          </div>
        }
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setSelectedCommission(null);
        }}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            ປິດ
          </Button>,
        ]}
        width={900}
      >
        {selectedCommission && (
          <div>
            <Descriptions bordered column={2} style={{ marginBottom: 24 }}>
              <Descriptions.Item label="ພະນັກງານ">
                <div>
                  <div style={{ fontWeight: 500 }}>
                    {selectedCommission.employee?.name}
                  </div>
                  <div style={{ fontSize: "12px", color: "#666" }}>
                    ID: {selectedCommission.employee?.employeeId} | ຕຳແໜ່ງ:{" "}
                    {selectedCommission.employee?.role}
                  </div>
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="ເດືອນ/ປີ">
                {commissionService.getMonthNameTH(selectedCommission.month)}{" "}
                {selectedCommission.year}
              </Descriptions.Item>
              <Descriptions.Item label="ຄ່າຄອມບໍລິການ">
                <Text style={{ color: "#1890ff", fontWeight: 500 }}>
                  {getTotalAmount(selectedCommission.totalServiceCommission)}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="ຄ່າຄອມແນະນຳ">
                <Text style={{ color: "#52c41a", fontWeight: 500 }}>
                  {getTotalAmount(selectedCommission.totalReferralCommission)}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="ຄ່າໂອທີ">
                <Text style={{ color: "#faad14", fontWeight: 500 }}>
                  {getTotalAmount(selectedCommission.totalOvertimeCommission)}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="ຍອດລວມ">
                <Text
                  style={{
                    color: "#f56a00",
                    fontWeight: 500,
                    fontSize: "16px",
                  }}
                >
                  {getTotalAmount(selectedCommission.grandTotal)}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="ສະຖານະ">
                <Badge
                  status={selectedCommission.isPaid ? "success" : "warning"}
                  text={selectedCommission.isPaid ? "ຈ່າຍແລ້ວ" : "ຍັງບໍ່ຈ່າຍ"}
                />
              </Descriptions.Item>
              {selectedCommission.isPaid && (
                <Descriptions.Item label="ຈ່າຍເມື່ອ">
                  <div>
                    <div>
                      {moment(selectedCommission.paidAt).format(
                        "DD/MM/YYYY HH:mm"
                      )}
                    </div>
                    <div style={{ fontSize: "12px", color: "#666" }}>
                      โดย: {selectedCommission.paidBy?.name} (
                      {selectedCommission.paidBy?.employeeId})
                    </div>
                  </div>
                </Descriptions.Item>
              )}
              {selectedCommission.notes && (
                <Descriptions.Item label="ໝາຍເຫດ" span={2}>
                  {selectedCommission.notes}
                </Descriptions.Item>
              )}
            </Descriptions>

            <Tabs defaultActiveKey="1">
              {/* Service Commissions */}
              <Tabs.TabPane
                tab={`ຄ່າຄອມບໍລິການ (${selectedCommission.serviceCommissions.length})`}
                key="1"
              >
                {selectedCommission.serviceCommissions.length > 0 ? (
                  <List
                    dataSource={selectedCommission.serviceCommissions}
                    renderItem={(item) => (
                      <List.Item>
                        <List.Item.Meta
                          avatar={
                            <Avatar
                              icon={<TeamOutlined />}
                              style={{ backgroundColor: "#1890ff" }}
                            />
                          }
                          title={
                            <div>
                              <span>{item.course?.name}</span>
                              <Tag color="blue" style={{ marginLeft: 8 }}>
                                {item.commissionRate}%
                              </Tag>
                              {item.isOvertime && <Tag color="orange">OT</Tag>}
                            </div>
                          }
                          description={
                            <div>
                              <div>
                                ວັນທີ:{" "}
                                {moment(item.serviceDate).format("DD/MM/YYYY")}
                              </div>
                              <div>ຊົ່ວໂມງເຮັດວຽກ: {item.hoursWorked} ຊົ່ວໂມງ.</div>
                              <div>
                                ຍອດ:{" "}
                                {formatCurrency(item.baseAmount, item.currency)}
                              </div>
                              <div
                                style={{ fontWeight: 500, color: "#f56a00" }}
                              >
                                ຄ່າຄອມ:{" "}
                                {formatCurrency(
                                  item.commissionAmount,
                                  item.currency
                                )}
                              </div>
                            </div>
                          }
                        />
                      </List.Item>
                    )}
                  />
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "40px 0",
                      color: "#666",
                    }}
                  >
                    ບໍ່ມີຄ່າຄອມມິດຊັ່ນຈາກການບໍລິການ
                  </div>
                )}
              </Tabs.TabPane>

              {/* Referral Commissions */}
              <Tabs.TabPane
                tab={`ຄ່າຄອມແນະນຳ (${selectedCommission.referralCommissions.length})`}
                key="2"
              >
                {selectedCommission.referralCommissions.length > 0 ? (
                  <List
                    dataSource={selectedCommission.referralCommissions}
                    renderItem={(item) => (
                      <List.Item>
                        <List.Item.Meta
                          avatar={
                            <Avatar
                              icon={<UserOutlined />}
                              style={{ backgroundColor: "#52c41a" }}
                            />
                          }
                          title={
                            <div>
                              <span>{item.customer?.name}</span>
                              <Tag color="green" style={{ marginLeft: 8 }}>
                                {item.commissionRate}%
                              </Tag>
                            </div>
                          }
                          description={
                            <div>
                              <div>ລູກຄ້າ ID: {item.customer?.customerId}</div>
                              <div>ເລກບິນ: {item.receipt?.receiptNumber}</div>
                              <div>
                                ວັນທີແນະນຳ:{" "}
                                {moment(item.referralDate).format("DD/MM/YYYY")}
                              </div>
                              <div>
                                ຍອດຂາຍ:{" "}
                                {formatCurrency(item.saleAmount, item.currency)}
                              </div>
                              <div
                                style={{ fontWeight: 500, color: "#f56a00" }}
                              >
                                ຄ່າຄອມ:{" "}
                                {formatCurrency(
                                  item.commissionAmount,
                                  item.currency
                                )}
                              </div>
                            </div>
                          }
                        />
                      </List.Item>
                    )}
                  />
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "40px 0",
                      color: "#666",
                    }}
                  >
                    ບໍ່ມີຄ່າຄອມມິດຊັ່ນຈາກການແນະນຳລູກຄ້າ
                  </div>
                )}
              </Tabs.TabPane>

              {/* Overtime Commissions */}
              <Tabs.TabPane
                tab={`ຄ່າໂອທີ (${selectedCommission.overtimeCommissions.length})`}
                key="3"
              >
                {selectedCommission.overtimeCommissions.length > 0 ? (
                  <List
                    dataSource={selectedCommission.overtimeCommissions}
                    renderItem={(item) => (
                      <List.Item>
                        <List.Item.Meta
                          avatar={
                            <Avatar
                              icon={<ClockCircleOutlined />}
                              style={{ backgroundColor: "#faad14" }}
                            />
                          }
                          title={
                            <div>
                              <span>
                                Record ID: {item.serviceRecord?.recordId}
                              </span>
                              <Tag color="orange" style={{ marginLeft: 8 }}>
                                OT x{item.overtimeRate}
                              </Tag>
                            </div>
                          }
                          description={
                            <div>
                              <div>ຊົ່ວໂມງໂອທີ: {item.overtimeHours} ชม.</div>
                              <div>
                                ອັດຕາ/ຊົ່ວໂມງ:{" "}
                                {formatCurrency(
                                  item.baseHourlyRate,
                                  item.currency
                                )}
                              </div>
                              <div>ອັດຕາໂອທີ: x{item.overtimeRate}</div>
                              <div
                                style={{ fontWeight: 500, color: "#f56a00" }}
                              >
                                ຄ່າໂອທີ:{" "}
                                {formatCurrency(
                                  item.overtimeAmount,
                                  item.currency
                                )}
                              </div>
                            </div>
                          }
                        />
                      </List.Item>
                    )}
                  />
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "40px 0",
                      color: "#666",
                    }}
                  >
                    ບໍ່ມີຄ່າໂອທີ
                  </div>
                )}
              </Tabs.TabPane>
            </Tabs>
          </div>
        )}
      </Modal>

      {/* Calculate Commission Modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center" }}>
            <CalculatorOutlined style={{ marginRight: 8 }} />
            ຄຳນວນຄ່າຄອມມິດປະຈຳເດືອນ
          </div>
        }
        open={calculateModalVisible}
        onCancel={() => {
          setCalculateModalVisible(false);
          calculateForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Alert
          message="ຄຳນວນຄ່າຄອມມິດຊັ່ນ"
          description="ລະບົບຈະຄຳນວນຄ່າຄອມມິດຊັ່ນຈາກການໃຫ້ບໍລິການ, ການແນະນຳລູກຄ້າ ແລະ ຄ່າໂອທີໃນການເດືອນທີ່ເລືອກ"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Spin spinning={calculateCommissionMutation.isLoading}>
          <Form
            form={calculateForm}
            layout="vertical"
            onFinish={handleCalculateSubmit}
          >
            <Form.Item
              name="employee"
              label="ເລືອກພະນັກງານ"
              rules={[{ required: true, message: "ກະລຸນາເລືອກພະນັກງານ" }]}
            >
              <Select
                placeholder="ເລືອກພະນັກງານ"
                showSearch
                optionFilterProp="children"
              >
                {employeesData?.data.employees?.map((emp: any) => (
                  <Option key={emp._id} value={emp._id}>
                    {emp.name} ({emp.employeeId})
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="month"
                  label="ເດືອນ"
                  rules={[{ required: true, message: "ກະລຸນາເລືອກເດືອນ" }]}
                  initialValue={moment().month() + 1}
                >
                  <Select>
                    {Array.from({ length: 12 }, (_, i) => (
                      <Option key={i + 1} value={i + 1}>
                        {commissionService.getMonthNameTH(i + 1)}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="year"
                  label="ປີ"
                  rules={[{ required: true, message: "ກະລຸນາເລືອກປີ" }]}
                  initialValue={moment().year()}
                >
                  <Select>
                    {Array.from({ length: 3 }, (_, i) => {
                      const year = moment().year() - i;
                      return (
                        <Option key={year} value={year}>
                          {year}
                        </Option>
                      );
                    })}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={calculateCommissionMutation.isLoading}
                >
                  ຄຳນວນຄ່າຄອມມິດຊັ່ນ
                </Button>
                <Button
                  onClick={() => {
                    setCalculateModalVisible(false);
                    calculateForm.resetFields();
                  }}
                  disabled={calculateCommissionMutation.isLoading}
                >
                  ຍົກເລິກ
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>

      {/* Pay Commission Modal */}
      <Modal
        title="ຈ່າຍຄ່າຄອມມິດຊັ່ນ"
        open={payModalVisible}
        onCancel={() => {
          setPayModalVisible(false);
          setSelectedCommission(null);
          payForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Spin spinning={payCommissionMutation.isLoading}>
          {selectedCommission && (
            <div
              style={{
                marginBottom: 16,
                padding: 12,
                backgroundColor: "#f6ffed",
                borderRadius: 6,
                border: "1px solid #b7eb8f",
              }}
            >
              <div>
                ພະນັກງານ: <Text strong>{selectedCommission.employee?.name}</Text>
              </div>
              <div>
                ເດືອນ/ປີ:{" "}
                <Text strong>
                  {commissionService.getMonthNameTH(selectedCommission.month)}{" "}
                  {selectedCommission.year}
                </Text>
              </div>
              <div>
                ຍອດລວມ:{" "}
                <Text
                  style={{
                    color: "#f56a00",
                    fontWeight: 500,
                    fontSize: "16px",
                  }}
                >
                  {getTotalAmount(selectedCommission.grandTotal)}
                </Text>
              </div>
            </div>
          )}

          <Form form={payForm} layout="vertical" onFinish={handlePaySubmit}>
            <Form.Item name="notes" label="ໝາຍເຫດ">
              <TextArea
                rows={4}
                placeholder="ໝາຍເຫດການຈ່າຍເງິນ (ບໍ່ບັງຄົບ)"
                showCount
                maxLength={500}
              />
            </Form.Item>

            <Alert
              message="ຢືນຢັນກນາຈ່າຍ"
              description="ເມື່ອກົດຢືນຢັນແລ້ວ ລະບົບຈະບັນທຶກການຈ່າຍຈ່າຍຄອມມິດຊັ່ນ ແລະ ບໍ່ສາມາດຍົກເລິກໄດ້"
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={payCommissionMutation.isLoading}
                  danger
                >
                  ຢືນຢັນການຈ່າຍ
                </Button>
                <Button
                  onClick={() => {
                    setPayModalVisible(false);
                    setSelectedCommission(null);
                    payForm.resetFields();
                  }}
                  disabled={payCommissionMutation.isLoading}
                >
                  ຍົກເລິກ
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>

      {/* Commission Summary Modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center" }}>
            <BarChartOutlined style={{ marginRight: 8 }} />
            ສະຫຼຸບຄ່າຄອມມິດຊັ່ນປະຈຳເດືອນ
          </div>
        }
        open={summaryModalVisible}
        onCancel={() => setSummaryModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setSummaryModalVisible(false)}>
            ปิด
          </Button>,
        ]}
        width={900}
      >
        <div style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Select
                value={selectedMonth}
                onChange={setSelectedMonth}
                style={{ width: "100%" }}
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <Option key={i + 1} value={i + 1}>
                    {commissionService.getMonthNameTH(i + 1)}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={12}>
              <Select
                value={selectedYear}
                onChange={setSelectedYear}
                style={{ width: "100%" }}
              >
                {Array.from({ length: 5 }, (_, i) => {
                  const year = moment().year() - i;
                  return (
                    <Option key={year} value={year}>
                      {year}
                    </Option>
                  );
                })}
              </Select>
            </Col>
          </Row>
        </div>

        <Spin spinning={isSummaryLoading}>
          {summaryData?.data && (
            <div>
              {/* Summary Statistics */}
              <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col span={6}>
                  <Card>
                    <Statistic
                      title="ພະນັກງານທັງໝົດ"
                      value={summaryData.data.summary.totalEmployees}
                      prefix={<TeamOutlined />}
                    />
                  </Card>
                </Col>
                <Col span={6}>
                  <Card>
                    <Statistic
                      title="ຈ່າຍແລ້ວ"
                      value={summaryData.data.summary.totalPaid}
                      prefix={<CheckCircleOutlined />}
                      valueStyle={{ color: "#3f8600" }}
                    />
                  </Card>
                </Col>
                <Col span={6}>
                  <Card>
                    <Statistic
                      title="ຍັງບໍ່ຈ່າຍ"
                      value={summaryData.data.summary.totalUnpaid}
                      prefix={<ClockCircleOutlined />}
                      valueStyle={{ color: "#faad14" }}
                    />
                  </Card>
                </Col>
                <Col span={6}>
                  <Card>
                    <Statistic
                      title="ຍອດລວມທັງໝົດ"
                      value={summaryData.data.summary.totalAmount.LAK.toLocaleString()}
                      suffix="₭"
                      prefix={<DollarOutlined />}
                      valueStyle={{ color: "#f56a00" }}
                    />
                  </Card>
                </Col>
              </Row>

              <Divider />

              {/* Individual Commissions */}
              <Title level={4}>รายละเอียดตามพนักงาน</Title>
              <Table
                dataSource={summaryData.data.commissions}
                pagination={false}
                size="small"
                columns={[
                  {
                    title: "ພະນັກງານ",
                    dataIndex: "employee",
                    render: (emp: any) => `${emp.name} (${emp.employeeId})`,
                  },
                  {
                    title: "ຍອດລວມ",
                    dataIndex: "grandTotal",
                    render: (totals: any) => getTotalAmount(totals),
                  },
                  {
                    title: "ສະຖານະ",
                    dataIndex: "isPaid",
                    render: (isPaid: boolean) => (
                      <Badge
                        status={isPaid ? "success" : "warning"}
                        text={isPaid ? "ຈ່າຍແລ້ວ" : "ຍັງບໍ່ຈ່າຍ"}
                      />
                    ),
                  },
                ]}
              />
            </div>
          )}
        </Spin>
      </Modal>
    </div>
  );
};



export default CommissionsPage;
